'use client';

import { cn } from '@lib/utils';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';

interface DropdownProps {
  items: string[];
  onItemSelect: (item: string) => void;
  className?: string;
  isOpen: boolean;
}

const Dropdown = ({
  items,
  onItemSelect,
  className,
  isOpen,
}: DropdownProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const parentRef = useRef<HTMLDivElement>(null);
  const isNoResult = items.length === 0;

  // 항목 높이 상수 정의
  const ITEM_HEIGHT = 48; // 각 항목의 높이 (h-12 = 48px)

  // 가상화 설정
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT, // 각 항목의 높이
    overscan: 5, // 화면 밖에 미리 렌더링할 항목 수
  });

  // 드롭다운 높이 계산
  const getDropdownHeight = () => {
    if (isNoResult) return '240px'; // 결과 없을 때 기본 높이

    // 항목이 5개 미만이면 항목 수에 맞게 높이 조정, 그렇지 않으면 최대 높이 240px
    return items.length < 5 ? `${items.length * ITEM_HEIGHT}px` : '240px';
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isNoResult) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev < items.length - 1 ? prev + 1 : 0));
          // 선택된 항목이 변경될 때 해당 항목으로 스크롤
          if (rowVirtualizer) {
            rowVirtualizer.scrollToIndex(
              selectedIndex + 1 < items.length ? selectedIndex + 1 : 0,
            );
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : items.length - 1));
          // 선택된 항목이 변경될 때 해당 항목으로 스크롤
          if (rowVirtualizer) {
            rowVirtualizer.scrollToIndex(
              selectedIndex - 1 >= 0 ? selectedIndex - 1 : items.length - 1,
            );
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < items.length) {
            onItemSelect(items[selectedIndex]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [items, selectedIndex, isNoResult, onItemSelect, rowVirtualizer]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute z-[1000] mt-[10px] w-full overflow-hidden rounded-md bg-white text-black shadow-lg',
        className,
      )}
    >
      <div className="flex h-12 items-center border-b border-solid border-gray-200 px-3 text-xs font-normal leading-4 tracking-[0.4px] text-3b-text-disabled">
        Result: {items.length}
      </div>

      {isNoResult ? (
        <div className="flex h-60 flex-shrink-0 cursor-pointer items-center justify-center px-3 text-base font-normal leading-[26px] tracking-[-0.176px] text-3b-text-disabled">
          No results found.
        </div>
      ) : (
        <div
          ref={parentRef}
          className="custom-scrollbar overflow-auto"
          style={{
            height: getDropdownHeight(), // 동적 높이 적용
            width: '100%',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
              const item = items[virtualRow.index];
              return (
                <div
                  key={virtualRow.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div
                    className={cn(
                      'flex h-12 flex-shrink-0 cursor-pointer items-center justify-start px-3 hover:bg-3b-background-medium',
                      selectedIndex === virtualRow.index &&
                        'bg-3b-background-medium',
                    )}
                    onClick={() => onItemSelect(item)}
                  >
                    {item}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
